/**
 * Chess.tsx - Main chess game component
 *
 * Refactored to use custom hooks for better separation of concerns.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from 'react-router-dom';

// Components
import ChessContainer from './ChessContainer';
import Moves from './Moves';
import PlayerTimer from './PlayerTimer';
import GameOverModal from './GameOverModal';

// Hooks
import {
    useChessSocket,
    useChessGame,
    useChessTimer,
    getAuthData
} from './hooks';

// Utilities
import { getPlayerColor, ChessColor } from './ChessLib';
import logger from '../../utils/logger';
import { ChessPiece, SquarePosition } from './types';

// Styles
import './Chess.css';

function GameChess() {
    const navigate = useNavigate();
    const { gameId } = useParams<{ gameId: string }>();

    // Local UI state
    const [showResignModal, setShowResignModal] = useState(false);
    const [showGameOverModal, setShowGameOverModal] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [initialJoinKey, setInitialJoinKey] = useState<string | null>(null);

    const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

    // Load stored auth on mount
    useEffect(() => {
        if (gameId) {
            const authData = getAuthData(gameId);
            if (authData?.joinKey) {
                setInitialJoinKey(authData.joinKey);
            }
        }
    }, [gameId]);

    // Socket hook - manages connection and events
    const {
        game,
        playerId,
        joined,
        error,
        joinGame,
        makeMove: socketMakeMove,
        resign,
        checkTime
    } = useChessSocket({
        gameId,
        serverUrl: SERVER_URL
    });

    // Game state hook - derived state and helpers
    const {
        gameStatus,
        isLoading,
        myColor,
        isMyTurn,
        whitePlayer,
        blackPlayer,
        isGameOver,
        didIWin,
        didIDraw,
        endReason,
        canJoin,
        canJoinAsWhite,
        canJoinAsBlack,
        toAlgebraicNotation
    } = useChessGame({
        game,
        playerId,
        joined
    });

    // Timer hook
    const { whiteTime, blackTime } = useChessTimer({
        game,
        isOngoing: gameStatus === 'ongoing',
        checkTime
    });

    // Show game over modal when game ends
    useEffect(() => {
        if (isGameOver && joined) {
            // Small delay to let the final state render
            const timer = setTimeout(() => {
                setShowGameOverModal(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isGameOver, joined]);

    // Auto-rejoin on mount if we have stored credentials
    useEffect(() => {
        if (gameId && initialJoinKey && game && !joined) {
            // Check if we can rejoin (disconnected player with matching key)
            const authData = getAuthData(gameId);
            if (authData) {
                const matchingPlayer = game.players.find(player =>
                    player.joinKey === authData.joinKey &&
                    player.disconnected === true
                );
                if (matchingPlayer) {
                    logger.info("Auto-rejoining game", { gameId });
                    joinGame(authData.joinKey);
                }
            }
        }
    }, [gameId, initialJoinKey, game, joined, joinGame]);

    // Make move handler - converts board positions to algebraic notation
    const handleMakeMove = useCallback((fromSquare: SquarePosition, toSquare: SquarePosition, piece: ChessPiece) => {
        if (gameStatus !== 'ongoing' || !isMyTurn || !game) {
            return;
        }

        const from = toAlgebraicNotation(fromSquare.row, fromSquare.col);
        const to = toAlgebraicNotation(toSquare.row, toSquare.col);

        socketMakeMove({ from, to });
    }, [gameStatus, isMyTurn, game, toAlgebraicNotation, socketMakeMove]);

    // UI Actions
    const handleResign = () => {
        resign();
        setShowResignModal(false);
    };

    const handleJoin = (joinKey?: string) => {
        joinGame(joinKey || initialJoinKey || undefined);
    };

    const navigateToHome = () => navigate("/mferchess");
    const navigateToReplay = () => gameId && navigate(`/mferchess/replay/${gameId}`);

    const copyShareLink = async () => {
        if (!gameId) return;
        const gameURL = `${window.location.origin}/mferchess/${gameId}`;
        try {
            await navigator.clipboard.writeText(gameURL);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 1000);
        } catch (err) {
            logger.error('Failed to copy link', { error: err });
        }
    };

    const shareOnX = () => {
        if (!game || !gameId) return;
        const gameURL = `${SERVER_URL}/mferchess/${gameId}`;
        const tweetText = `♟️♟️ mfers arcade game alert ♟️♟️\n\nCheck out the chess game ${game.gameName} on mfers arcade!\n\n👇👇👇`;
        const twitterIntentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(gameURL)}`;
        window.open(twitterIntentUrl, '_blank', 'noopener,noreferrer');
    };

    const handlePlayAgain = () => {
        setShowGameOverModal(false);
        navigateToHome();
    };

    // Render helpers
    const renderGameStatus = () => {
        if (gameStatus === 'waiting') {
            return (
                <span style={{
                    color: 'black',
                    fontWeight: 'bold',
                    fontSize: '27px',
                    backgroundColor: '#999',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    display: 'inline-block'
                }}>
                    Waiting for players
                </span>
            );
        }
        if (gameStatus === 'ongoing') {
            return (
                <span style={{
                    color: 'black',
                    fontWeight: 'bold',
                    fontSize: '27px',
                    backgroundColor: '#FFD700',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    display: 'inline-block'
                }}>
                    Ongoing
                </span>
            );
        }
        if (gameStatus === 'completed') {
            const backgroundColor = didIWin ? '#00AA00' : '#FF0000';
            const text = joined ? (didIWin ? 'You Won!' : 'You Lost') : 'Game Over';
            return (
                <span style={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '27px',
                    backgroundColor,
                    padding: '10px 20px',
                    borderRadius: '12px',
                    display: 'inline-block'
                }}>
                    {text}
                </span>
            );
        }
        return null;
    };

    const renderPlayerRow = (color: 'white' | 'black') => {
        const isWhite = color === 'white';
        const player = isWhite ? whitePlayer : blackPlayer;
        const playerTime = isWhite ? whiteTime : blackTime;
        const canJoinSlot = isWhite ? canJoinAsWhite : canJoinAsBlack;
        const isCurrentPlayerTurn = game && getPlayerColor(game, game.currentPlayer) === (isWhite ? ChessColor.WHITE : ChessColor.BLACK);
        const isWinner = game?.winnerId === player?.id;
        const isMe = playerId === player?.id;
        const isAI = player?.socketId?.startsWith('AI');

        return (
            <tr style={{ backgroundColor: isWhite ? '#ffffff' : '#a9a9a9' }}>
                <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img
                            src={`/images/chess/p-${isWhite ? 'white' : 'black'}.png`}
                            alt={`Player ${isWhite ? 1 : 2}`}
                            style={{ width: '30px', height: '30px' }}
                        />
                        <span>
                            Player {isWhite ? 1 : 2} ({player?.id.substring(0, 6) || "Open Slot"})
                        </span>
                        {isAI && <span style={{ fontSize: '24px' }} title="AI Player">🤖</span>}
                        {joined && isMe && (
                            <span style={{ color: '#666', fontStyle: 'italic' }}>(You)</span>
                        )}
                    </div>
                </td>
                <td>
                    {gameStatus === 'ongoing' && isCurrentPlayerTurn && <span>→</span>}
                    {gameStatus === 'completed' && isWinner && <span>👑</span>}
                </td>
                <td>
                    {player ? (
                        <PlayerTimer
                            player={player}
                            isPlayerTurn={gameStatus === 'ongoing' && !!isCurrentPlayerTurn}
                        />
                    ) : (
                        <span>--:--</span>
                    )}
                </td>
                <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-start', alignItems: 'center' }}>
                        {/* Join button */}
                        {!joined && canJoinSlot && (
                            <button onClick={() => handleJoin()}>Join</button>
                        )}

                        {/* Player actions when joined and it's my row */}
                        {joined && isMe && (
                            <>
                                {gameStatus === 'ongoing' && (
                                    <button
                                        onClick={() => setShowResignModal(true)}
                                        style={{
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            padding: '8px 20px',
                                            fontSize: '18px',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Resign
                                    </button>
                                )}
                                {game && game.players.length < 2 && gameStatus === 'ongoing' && (
                                    <button
                                        onClick={copyShareLink}
                                        style={{
                                            backgroundColor: linkCopied ? '#00AA00' : 'black',
                                            color: 'white',
                                            fontSize: '18px',
                                            fontFamily: "'Sartoshi', sans-serif",
                                            padding: '8px 20px',
                                            cursor: 'pointer',
                                            border: linkCopied ? '2px solid #00AA00' : '2px solid #333',
                                            borderRadius: '4px',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        {linkCopied ? '✓' : 'Copy Link'}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </td>
            </tr>
        );
    };

    // Error state
    if (error === 'Game does not exist' || gameStatus === 'error') {
        return (
            <div className="game-container">
                <h2>Mfer Chess</h2>
                <p><span>Game Not Found!</span></p>
                <p>
                    <button onClick={navigateToHome} className="back-button">
                        Back to Home
                    </button>
                </p>
            </div>
        );
    }

    // Loading state
    if (isLoading || !game) {
        return (
            <div className="game-container">
                <div className="loading-modal">Loading...</div>
            </div>
        );
    }

    return (
        <div className="game-container">
            {/* Row 1: Title and Game State */}
            <div className="title-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <h1 style={{ fontSize: '48px', margin: '0' }}>
                        Game: {game.gameName || gameId?.substring(0, 8)}
                    </h1>
                    {renderGameStatus()}
                </div>
            </div>

            {/* Row 2: Player Status Table */}
            <div className="chess-live-player-status">
                <div className="chess-live-box">
                    <table className="chess-live-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th></th>
                                <th>Time</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderPlayerRow('white')}
                            {renderPlayerRow('black')}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Row 3: Board */}
            <div className="board-section">
                <div className="board-wrapper">
                    <ChessContainer
                        game={game}
                        playerId={playerId}
                        makeMove={handleMakeMove}
                    />
                </div>
            </div>

            {/* Row 4: Move History */}
            <div className="chess-new-moves-section">
                <div className="chess-new-moves-box">
                    <Moves game={game} />
                </div>
            </div>

            {/* Back to Home and Share */}
            <div style={{ textAlign: 'center', marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
                <button onClick={navigateToHome} className="back-button">
                    Back to Home
                </button>
                {gameStatus === 'waiting' && (
                    <button onClick={shareOnX} className="share-button share-x-button">
                        <img src="/images/x-logo-black.png" alt="X" className="x-logo-small-button" />
                        Share on X
                    </button>
                )}
            </div>

            {/* Resign Confirmation Modal */}
            {showResignModal && (
                <div className="modal-overlay" onClick={() => setShowResignModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Confirm Resignation</h3>
                        <p>Are you sure you want to resign? This will end the game and you will lose.</p>
                        <div className="modal-buttons">
                            <button onClick={handleResign} className="confirm-button">
                                Yes, Resign
                            </button>
                            <button onClick={() => setShowResignModal(false)} className="cancel-button">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Game Over Modal */}
            {showGameOverModal && gameId && (
                <GameOverModal
                    game={game}
                    gameId={gameId}
                    playerId={playerId}
                    didIWin={didIWin}
                    didIDraw={didIDraw}
                    endReason={endReason}
                    onPlayAgain={handlePlayAgain}
                    onClose={() => setShowGameOverModal(false)}
                />
            )}
        </div>
    );
}

export default GameChess;
